/**
 * Zakładka „Załączniki" faktury zakupu/sprzedaży — zwykła sekcja załączników + przypięty
 * kafelek „Faktura KSeF (PDF)", gdy faktura pochodzi z KSeF. Klik w kafelek otwiera drawer
 * z oficjalnym PDF-em generowanym w locie z XML-a (GET /api/documents/:id/ksef-source zwraca
 * XML + link weryfikacyjny; dla faktury spoza KSeF zwraca `null` i kafelka nie ma).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';

import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { KsefPdfDrawer } from '@/components/ksef/KsefPdfDrawer';
import { api } from '@/lib/api';

interface KsefSourceDto {
  ksefInvoiceId: string;
  ksefNumber: string;
  invoiceNumber: string | null;
  xml: string;
  verificationUrl: string | null;
  acquisitionDate: string | null;
}

export interface InvoiceAttachmentsSectionProps {
  documentId: string;
  companyId?: string;
}

export function useDocumentKsefSource(documentId: string) {
  return useQuery({
    queryKey: ['document-ksef-source', documentId],
    queryFn: async () => {
      const res = await api.get<{ source: KsefSourceDto | null }>(
        `/documents/${documentId}/ksef-source`,
      );
      return res.data.source;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function InvoiceAttachmentsSection({
  documentId,
  companyId,
}: InvoiceAttachmentsSectionProps) {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const sourceQ = useDocumentKsefSource(documentId);
  const source = sourceQ.data ?? null;

  const pinnedItem = source ? (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setPreviewOpen(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setPreviewOpen(true);
        }
      }}
      data-testid="ksef-pdf-attachment"
      className="flex cursor-pointer items-center gap-3 rounded-lg border border-accent-500/40 bg-accent-500/5 px-3 py-2.5 transition-colors hover:bg-accent-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded border border-border-secondary bg-bg-elevated text-error-500">
        <FileText className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">
          {t('ksefPdf.attachmentName')}
        </p>
        <p className="truncate font-mono text-xs text-text-tertiary">{source.ksefNumber}</p>
      </div>
      <span className="flex-shrink-0 rounded bg-accent-500/10 px-1.5 py-0.5 text-[11px] font-medium text-accent-600">
        {t('ksefPdf.badge')}
      </span>
    </div>
  ) : undefined;

  return (
    <>
      <AttachmentsSection
        ownerType="document"
        ownerId={documentId}
        companyId={companyId}
        pinnedItem={pinnedItem}
      />
      {source && (
        <KsefPdfDrawer
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          source={source}
          fileBaseName={source.invoiceNumber ?? source.ksefNumber}
        />
      )}
    </>
  );
}
